/**
 * SequenceModal — kicks off a multi-step cold-email sequence on a saved
 * lead via /api/outreach/leads/{lead_id}/start-sequence (which wraps the
 * Apify outreach-sequencer actor).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Send, Loader2, X, Calendar, Paperclip, FileText } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import IntentSignalsPanel from "./IntentSignalsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BODY_PT = `Olá {name},

Vi seu trabalho na {sector} aí em {city} e queria fazer uma proposta rápida — temos rodado leads QUENTES com agentes locais que normalmente fecham 2-3 jobs por semana.

Tem 10 minutos esta semana pra eu te mostrar como funciona?

— [seu nome]`;

const DEFAULT_BODY_EN = `Hi {name},

I came across your work in {sector} in {city} and wanted to share a quick idea — we've been routing HOT local leads to agents who typically close 2-3 jobs per week.

Got 10 minutes this week so I can show you how it works?

— [your name]`;

// Iter52 — extra preset templates available in the cold-email sequence modal.
// "lead_not_found" is the user-requested template for cases where direct
// contact info couldn't be enriched and we need to ask whoever opens the mail
// to forward / supply the right address.
const TEMPLATES = {
  default: { id: "default", label_pt: "Padrão (HOT leads)", label_en: "Default (HOT leads)",
             subject_pt: "Leads quentes na região de {city} pra você",
             subject_en: "Hot leads in {city} for your team",
             body_pt: DEFAULT_BODY_PT, body_en: DEFAULT_BODY_EN },
  lead_not_found: { id: "lead_not_found",
             label_pt: "🔍 Lead Não Encontrado",
             label_en: "🔍 Lead Not Found",
             subject_pt: "Tentando alcançar {name} em {company}",
             subject_en: "Trying to reach {name} at {company}",
             body_pt: `Olá,

Tentei localizar o contato direto de {name} em {company} mas não achei o e-mail certo — peço desculpas pelo workaround.

Se você não é a pessoa certa para falar sobre prospecção / geração de leads em {sector}, poderia me indicar quem é? Ou apenas reenviar este e-mail pro responsável de marketing/vendas.

Anexo um material rápido (PDF) mostrando como ajudamos 3 empresas em {city} a encherem o pipeline o último trimestre.

Obrigado pela ajuda!
— [seu nome]`,
             body_en: `Hi there,

I tried reaching {name} at {company} but couldn't pin down their direct email — sorry for the workaround.

If you're not the right contact for prospecting / lead-gen in {sector}, would you mind pointing me in the right direction? Or just forwarding this to whoever owns marketing/sales.

I attached a quick PDF of how we helped 3 firms in {city} fill their pipeline last quarter.

Thanks!
— [your name]` },
  follow_up: { id: "follow_up",
             label_pt: "↩️ Follow-up curto",
             label_en: "↩️ Short follow-up",
             subject_pt: "Re: Leads quentes em {city}",
             subject_en: "Re: Hot leads in {city}",
             body_pt: `Oi {name},

Só passando rapidinho — viu meu e-mail anterior?

Se faz mais sentido eu mandar uma demo de 5 min em vídeo, é só me avisar.

— [seu nome]`,
             body_en: `Hey {name},

Just bumping this — did you see my previous note?

If a 5-min video walkthrough would be easier, just say the word.

— [your name]` },
};

export default function SequenceModal({ open, onClose, lead, onSent }) {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [tplId, setTplId] = useState("default");
  const [subject, setSubject] = useState(isPt
    ? "Leads quentes na região de {city} pra você"
    : "Hot leads in {city} for your team");
  const [body, setBody] = useState(isPt ? DEFAULT_BODY_PT : DEFAULT_BODY_EN);
  const [days, setDays] = useState("1, 3, 7");
  const [busy, setBusy] = useState(false);
  // Iter52 — generic file attachment for the cold-email sequence (PDF/CSV/etc).
  // Note: the actual attachment delivery requires a real SMTP/SendGrid provider;
  // when running in the Apify sequencer simulated mode, the file metadata is
  // recorded but not delivered.
  const [attachment, setAttachment] = useState(null);

  if (!lead) return null;

  // Apply selected template
  const applyTemplate = (id) => {
    const tpl = TEMPLATES[id];
    if (!tpl) return;
    setTplId(id);
    setSubject(isPt ? tpl.subject_pt : tpl.subject_en);
    setBody(isPt ? tpl.body_pt : tpl.body_en);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const seq = days.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => n > 0);
      // Replace placeholders in subject/body before sending
      const repl = (s) => s
        .replace(/\{name\}/g, lead.name || "")
        .replace(/\{company\}/g, lead.company || lead.name || "")
        .replace(/\{sector\}/g, lead.sector || "")
        .replace(/\{city\}/g, (lead.address || "").split(",").slice(-2, -1)[0] || "");
      const { data } = await axios.post(`${API}/outreach/leads/${lead.id}/start-sequence`, {
        sequence_days: seq.length ? seq : [1, 3, 7],
        subject: repl(subject),
        body: repl(body),
        attachment_meta: attachment
          ? { name: attachment.name, size: attachment.size, type: attachment.type }
          : null,
        template_id: tplId,
      });
      if (data.simulated) {
        toast.warning(isPt
          ? "Sequência registrada como SIMULADA — configure o provedor de email para enviar de verdade."
          : "Sequence logged as SIMULATED — configure the email provider to actually send.");
      } else {
        toast.success(isPt
          ? `Sequência iniciada (run ${data.run_id || "?"})`
          : `Sequence started (run ${data.run_id || "?"})`);
      }
      onSent && onSent(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="sequence-modal"
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[18px] font-bold">
            <Send className="w-5 h-5 text-[#3B82F6]" />
            {isPt ? "Iniciar sequência de cold-email" : "Start cold-email sequence"}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[#6B7280]">
            <b>{lead.name}</b> · {lead.email || lead.contact_email || (isPt ? "(sem email — enriqueça primeiro)" : "(no email — enrich first)")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-3">
          {/* Intent Signals — Perplexity-powered real-time company signals. */}
          <IntentSignalsPanel
            lead={lead}
            senderName=""
            valueProp=""
            onApply={({ subject: s, body: b }) => {
              if (s) setSubject(s);
              if (b) setBody(b);
              setTplId("intent_signals");
            }}
          />

          {/* Iter52 — Template picker (Lead Not Found, Default, Follow-up). */}
          <label className="block">
            <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">
              {isPt ? "Template" : "Template"}
            </div>
            <select value={tplId} onChange={(e) => applyTemplate(e.target.value)}
              data-testid="sequence-template"
              className="w-full al-input h-10 text-[13px]">
              {Object.values(TEMPLATES).map((tpl) => (
                <option key={tpl.id} value={tpl.id}>{isPt ? tpl.label_pt : tpl.label_en}</option>
              ))}
            </select>
            <p className="text-[10.5px] text-[#9CA3AF] mt-1.5">
              {isPt
                ? "Suporta variáveis: {name}, {company}, {sector}, {city}."
                : "Supports variables: {name}, {company}, {sector}, {city}."}
            </p>
          </label>

          <label className="block">
            <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">
              {isPt ? "Assunto" : "Subject"}
            </div>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
              data-testid="sequence-subject"
              className="w-full al-input h-10 text-[13px]" />
          </label>
          <label className="block">
            <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5">
              {isPt ? "Corpo (suporta {name}, {company}, {sector}, {city})" : "Body (supports {name}, {company}, {sector}, {city})"}
            </div>
            <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)}
              data-testid="sequence-body"
              className="w-full al-input p-3 text-[12.5px] font-mono leading-relaxed" />
          </label>

          {/* Iter52 — Attachment slot (PDF/CSV/image/docx). */}
          <div className="rounded-lg border border-dashed border-[#E5E7EB] bg-[#F9FAFB] p-3" data-testid="sequence-attachment-box">
            {!attachment ? (
              <label className="flex items-center justify-between gap-2 cursor-pointer">
                <div className="flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-[#6B7280]" />
                  <span className="text-[12.5px] text-[#6B7280] font-medium">
                    {isPt ? "Anexar arquivo (PDF, CSV, imagem, docx)" : "Attach file (PDF, CSV, image, docx)"}
                  </span>
                </div>
                <span className="text-[10.5px] uppercase font-bold tracking-wider text-[#3B82F6]">
                  {isPt ? "Escolher" : "Choose"}
                </span>
                <input type="file"
                  data-testid="sequence-attachment-input"
                  accept=".pdf,.csv,.txt,.png,.jpg,.jpeg,.webp,.docx,.xlsx"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 10 * 1024 * 1024) {
                      toast.error(isPt ? "Arquivo > 10 MB. Use um menor." : "File > 10 MB. Use a smaller one.");
                      e.target.value = "";
                      return;
                    }
                    setAttachment({ name: f.name, size: f.size, type: f.type, file: f });
                    e.target.value = "";
                  }} />
              </label>
            ) : (
              <div className="flex items-center justify-between gap-2" data-testid="sequence-attachment-row">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-[#3B82F6] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#111827] truncate">{attachment.name}</div>
                    <div className="text-[10.5px] text-[#6B7280]">
                      {(attachment.size / 1024).toFixed(1)} KB · {attachment.type || "file"}
                    </div>
                  </div>
                </div>
                <button type="button" onClick={() => setAttachment(null)}
                  data-testid="sequence-attachment-remove"
                  className="text-[#6B7280] hover:text-[#DC2626] p-1">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {attachment && (
              <p className="text-[10.5px] text-[#9CA3AF] italic mt-2">
                {isPt
                  ? "Aviso: o anexo será incluído quando o provedor SMTP/SendGrid estiver configurado. Em modo simulado, registramos só os metadados."
                  : "Heads-up: the attachment is delivered once an SMTP/SendGrid provider is configured. In simulated mode we only record metadata."}
              </p>
            )}
          </div>

          <label className="block">
            <div className="text-[10.5px] uppercase text-[#6B7280] font-semibold mb-1.5 inline-flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> {isPt ? "Cadência (dias, separados por vírgula)" : "Cadence (days, comma-separated)"}
            </div>
            <input type="text" value={days} onChange={(e) => setDays(e.target.value)}
              data-testid="sequence-days"
              className="w-full al-input h-9 text-[12.5px] tabular-nums" placeholder="1, 3, 7" />
            <p className="text-[10.5px] text-[#9CA3AF] mt-1.5">
              {isPt
                ? "Ex.: 1,3,7 → envio inicial + follow-up em D3 + reforço em D7."
                : "Ex.: 1,3,7 → initial send + follow-up on D3 + reinforce on D7."}
            </p>
          </label>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2 pt-3 border-t border-[#E5E7EB]">
          <button type="button" onClick={onClose}
            className="px-4 h-9 rounded-lg border border-[#E5E7EB] text-[12.5px] font-semibold hover:bg-[#FAFAFB] inline-flex items-center gap-1.5">
            <X className="w-3.5 h-3.5" /> {isPt ? "Cancelar" : "Cancel"}
          </button>
          <button type="button" onClick={submit} disabled={busy}
            data-testid="sequence-submit"
            className="px-5 h-9 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {isPt ? "Iniciar sequência" : "Start sequence"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
